// src/pages/SelectProfilePage.js
import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import api from '../api';

const SelectProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const matchedPlayers = location.state?.matched_players;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If no matched players in state, redirect back to register
  if (!matchedPlayers || matchedPlayers.length === 0) {
    return <Navigate to="/register" replace />;
  }

  const handleSelectPlayer = async (playerId) => {
    setError('');
    setLoading(true);

    try {
      await api.post('/api/auth/link-profile/', { player_id: playerId });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to link profile. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gray-900/60" />

      {/* Content */}
      <div className="relative w-full max-w-lg p-6 sm:p-8 bg-gray-900/10 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 z-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/15 flex items-center justify-center">
            <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Select Your Profile</h2>
          <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
            We found multiple academy profiles linked to this email. Who is using this device?
          </p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 p-3 rounded-lg mb-6 text-sm text-center">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {matchedPlayers.map((player) => (
            <button
              key={player.id}
              onClick={() => handleSelectPlayer(player.id)}
              disabled={loading}
              className="w-full flex items-center gap-4 p-4 bg-gray-800/60 hover:bg-gray-700/80 border border-gray-600/50 hover:border-teal-500/40 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="w-11 h-11 flex-shrink-0 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-lg group-hover:bg-teal-500/30 transition-colors">
                {player.first_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="text-left">
                <div className="font-semibold text-white text-lg">
                  {player.first_name} {player.last_name}
                </div>
                <div className="text-xs text-gray-400">Academy Player Profile</div>
              </div>
              <svg className="w-5 h-5 text-gray-500 ml-auto group-hover:text-teal-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>

        {loading && (
          <p className="text-center text-sm text-gray-400 mt-4 animate-pulse">
            Linking profile...
          </p>
        )}
      </div>
    </div>
  );
};

export default SelectProfilePage;
